import Card from "@/components/common/Card";
import { useTheme } from "@/hooks/useTheme";
import {
  BellIcon,
  LanguageIcon,
  LockClosedIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

interface SettingsSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const SettingsSidebar = ({ activeSection, onSectionChange }: SettingsSidebarProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const sections = [
    {
      id: "account",
      label: t("settings.account"),
      icon: UserIcon,
      isActive: true,
    },
    {
      id: "notifications",
      label: t("settings.notifications"),
      icon: BellIcon,
    },
    {
      id: "language",
      label: t("settings.language"),
      icon: LanguageIcon,
    },
    {
      id: "theme",
      label: t("settings.theme"),
      icon: theme === "dark" ? MoonIcon : SunIcon,
    },
    {
      id: "privacy",
      label: t("settings.privacy"),
      icon: ShieldCheckIcon,
    },
    {
      id: "security",
      label: t("settings.security"),
      icon: LockClosedIcon,
    },
  ];

  return (
    <div className="md:col-span-1">
      <div className="space-y-4">
        <Card className="overflow-hidden p-0">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            
            return (
              <div
                key={section.id}
                onClick={() => onSectionChange(section.id)}
                className={`flex items-center border-l-4 p-4 cursor-pointer transition-colors ${
                  isActive
                    ? "border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20"
                    : "border-transparent hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-800/50"
                }`}
              >
                <Icon className={`h-5 w-5 ${
                  isActive
                    ? "text-primary-500 dark:text-primary-400"
                    : "text-gray-400 dark:text-gray-500"
                }`} />
                <span className={`ml-3 text-sm font-medium ${
                  isActive
                    ? "text-primary-700 dark:text-primary-300"
                    : "text-gray-600 dark:text-gray-300"
                }`}>
                  {section.label}
                </span>
              </div>
            );
          })}
        </Card>

        {/* Session Info Card */}
        <Card className="p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.sessionInfo")}
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("settings.lastLogin")}
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {new Date().toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("settings.browser")}
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {navigator.userAgent.split(" ").slice(-1)[0].split("/")[0]}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("settings.location")}
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Ethiopia
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SettingsSidebar;

